import { CoreAPI, createAPIMethod } from '@hero/sdk-core';
import {
  AppApplyEarlyAccessAPI,
  AppGetAllAPI,
  AppGetOrderWaybillBayanAPI,
  AppGetWaybillBayanPdfAPI,
  AppInstalledGetAllAPI,
  AppIssueWaybillBayanAPI,
  AppLogistiGoodTypesAPI,
  AppLogistiInstallAPI,
  AppLogistiUninstallAPI,
  AppLogistiUpdateSettingsAPI,
} from '@hero/api';

export function createAppsAPI(coreAPI: CoreAPI) {
  return {
    /**
     * The Apps & Integrations catalog, localized to the request language.
     *
     * API: {@link AppGetAllAPI}
     */
    getAll: createAPIMethod<AppGetAllAPI>(coreAPI, 'apps.getAll'),

    /**
     * Register interest / apply for early access to an app that has no
     * connector yet.
     *
     * API: {@link AppApplyEarlyAccessAPI}
     */
    applyEarlyAccess: createAPIMethod<AppApplyEarlyAccessAPI>(coreAPI, 'apps.applyEarlyAccess'),

    /**
     * The caller's installs, with the provider account id masked.
     *
     * Also answers "can this tenant file right now" through `canIssue` /
     * `filingMode` on each row. There was a separate `logistiCapability`
     * method for that, from when platform filing wrote no install row; the
     * two became one fact behind two calls, and the surfaces reading the
     * wrong one offered Connect to tenants who were already issuing.
     *
     * API: {@link AppInstalledGetAllAPI}
     */
    getInstalled: createAPIMethod<AppInstalledGetAllAPI>(coreAPI, 'apps.getInstalled'),

    // ─── Logisti / Bayan connector ────────────────────────────────────────
    //
    // Every method below is scoped to the caller by the backend, which derives
    // the owner from the auth token. None of them take an org id.

    /**
     * Connect Logisti — the tenant's own Bayan account, or Hero's licence on
     * its behalf. For `own_account` the backend verifies the account id
     * against Bayan before saving, so a rejected credential fails here rather
     * than silently at the first waybill.
     *
     * API: {@link AppLogistiInstallAPI}
     */
    logistiInstall: createAPIMethod<AppLogistiInstallAPI>(coreAPI, 'apps.logistiInstall'),

    /**
     * Uninstall. Drops the credentials but keeps the audit trail, so
     * already-issued waybills stay attributable.
     *
     * API: {@link AppLogistiUninstallAPI}
     */
    logistiUninstall: createAPIMethod<AppLogistiUninstallAPI>(coreAPI, 'apps.logistiUninstall'),

    /**
     * Update trigger / commercial-term settings.
     *
     * API: {@link AppLogistiUpdateSettingsAPI}
     */
    logistiUpdateSettings: createAPIMethod<AppLogistiUpdateSettingsAPI>(
      coreAPI,
      'apps.logistiUpdateSettings',
    ),

    /**
     * Cargo types the Org may put on a waybill, leaves only.
     *
     * Feeds both the "default cargo type" setting and the per-order picker.
     * Bayan rejects a parent node, so the backend filters branches out rather
     * than letting a dropdown offer a choice that fails at issue time.
     *
     * API: {@link AppLogistiGoodTypesAPI}
     */
    logistiGoodTypes: createAPIMethod<AppLogistiGoodTypesAPI>(coreAPI, 'apps.logistiGoodTypes'),

    /**
     * Government-waybill state for one order, including the blockers that
     * still need fixing before it can be issued.
     *
     * API: {@link AppGetOrderWaybillBayanAPI}
     */
    logistiWaybillStatus: createAPIMethod<AppGetOrderWaybillBayanAPI>(
      coreAPI,
      'apps.logistiWaybillStatus',
    ),

    /**
     * Issue or retry the government waybill. Idempotent — an order that
     * already has a trip returns its existing state.
     *
     * API: {@link AppIssueWaybillBayanAPI}
     */
    logistiIssueWaybill: createAPIMethod<AppIssueWaybillBayanAPI>(
      coreAPI,
      'apps.logistiIssueWaybill',
    ),

    /**
     * The Bayan-issued PDF, base64 encoded.
     *
     * API: {@link AppGetWaybillBayanPdfAPI}
     */
    logistiWaybillPdf: createAPIMethod<AppGetWaybillBayanPdfAPI>(
      coreAPI,
      'apps.logistiWaybillPdf',
    ),
  };
}
